import React, { useState } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, Loader2, Download } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { STATUS_META } from "@/lib/services";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function BookingStatus() {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  const search = async () => {
    if (!query.trim()) { toast.error("Enter booking reference or mobile number"); return; }
    setLoading(true);
    try {
      const isRef = /^TTD/i.test(query.trim());
      const params = isRef ? { ref: query.trim() } : { mobile: query.trim() };
      const { data } = await api.get("/bookings/status", { params });
      setResults(data);
      if (data.length === 0) toast.info("No bookings found for this search");
    } catch { toast.error("Search failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="App min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 max-w-4xl mx-auto w-full px-4 py-10">
        <div className="text-center mb-6">
          <h1 className="font-cinzel text-3xl font-bold text-amber-950">Check Booking Status</h1>
          <p className="text-slate-500 text-sm mt-1">Enter your Booking Reference (TTDXXXX) or registered Mobile Number</p>
          <div className="sacred-divider w-40 mx-auto mt-4" />
        </div>

        <div className="flex gap-2 max-w-xl mx-auto">
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && search()}
            placeholder="e.g. TTD1A2B3C4D or 9876543210"
            data-testid="booking-status-search-input"
            className="flex-1"
          />
          <Button onClick={search} disabled={loading} className="bg-[#E65100] hover:bg-[#BF360C] text-white" data-testid="booking-status-search-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          </Button>
        </div>

        <div className="mt-8 space-y-4">
          {results && results.map((b) => {
            const meta = STATUS_META[b.status] || STATUS_META.pending;
            return (
              <div key={b.reference} className="bg-white border border-amber-200 rounded-xl p-5 shadow-sm" data-testid={`status-card-${b.reference}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-bold text-[#7A0016]">{b.reference}</div>
                    <div className="font-semibold text-amber-950">{b.service_name}</div>
                  </div>
                  <span className={`text-xs font-bold px-3 py-1 rounded-full border ${meta.color}`}>{meta.label}</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 text-sm">
                  <div><div className="text-xs text-slate-400">Date</div><div className="font-medium">{b.booking_date}</div></div>
                  <div><div className="text-xs text-slate-400">Pilgrims</div><div className="font-medium">{b.num_pilgrims}</div></div>
                  <div><div className="text-xs text-slate-400">Lead</div><div className="font-medium">{b.lead_name}</div></div>
                  <div><div className="text-xs text-slate-400">Amount</div><div className="font-medium text-[#E65100]">₹{Number(b.amount).toLocaleString("en-IN")}</div></div>
                </div>
                {b.status === "confirmed" && (
                  <a
                    href={`${BACKEND}/api/bookings/${b.reference}/ticket`}
                    className="mt-4 inline-flex items-center gap-2 bg-[#7A0016] hover:bg-[#50000E] text-amber-50 text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
                    data-testid={`download-ticket-${b.reference}`}
                  >
                    <Download className="w-4 h-4" /> Download e-Ticket (PDF)
                  </a>
                )}
              </div>
            );
          })}
        </div>
      </main>
      <Footer />
    </div>
  );
}
